// Results.jsx (drop-in)
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { byPrefixAndName } from '@awesome.me/kit-f089350b06/icons';
import { useNavigate } from 'react-router';
import Skeleton from '../Skeleton';
import * as S from '../../styled';

const Results = ({
  visibleGroups,
  activeResultIndex,
  setRowElementByIndex,
  query,
  isSearching,
  hasSearched,
  onClose,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const trimmedQuery = (query ?? '').trim();

  const nonEmptyGroups = useMemo(
    () => visibleGroups.filter((group) => group.items.length > 0),
    [visibleGroups],
  );

  const totalItems = useMemo(
    () => nonEmptyGroups.reduce((count, group) => count + group.items.length, 0),
    [nonEmptyGroups],
  );

  const showStartTyping = trimmedQuery.length === 0 && !isSearching;

  const showNoResults = trimmedQuery.length > 0 && hasSearched && !isSearching && totalItems === 0;

  // While searching: skeleton rows when there's nothing on screen yet, or a
  // dimming overlay (no spinner) when previous results are being refreshed.
  const isRefreshing = isSearching && trimmedQuery.length > 0;
  const showSkeleton = isRefreshing && totalItems === 0;
  const showRefreshOverlay = isRefreshing && totalItems > 0;

  let flatIndexCounter = -1;

  return (
    <S.Body>
      <S.ResultsScroll data-scroll-lock-ignore>
        {showStartTyping && (
          <S.EmptyState>
            <S.EmptyIcon>
              <FontAwesomeIcon icon={byPrefixAndName.fas['magnifying-glass']} size="3x" />
            </S.EmptyIcon>
            <S.EmptyTitle>
              {t('search:common.empty.title', 'Begin met typen om te zoeken')}
            </S.EmptyTitle>
            <S.EmptySubtitle>
              {t('search:common.empty.subtitle', 'Zoek in domeinen, tickets en meer.')}
            </S.EmptySubtitle>
          </S.EmptyState>
        )}

        {showSkeleton && <Skeleton />}

        {showNoResults && (
          <S.EmptyState>
            <S.NoResultIcon>
              <FontAwesomeIcon icon={byPrefixAndName.far['face-thinking']} size="3x" />
            </S.NoResultIcon>
            <S.EmptyTitle>{t('search:common.noResults.title', 'Geen resultaten')}</S.EmptyTitle>
            <S.EmptySubtitle>
              {t('search:common.noResults.subtitle', 'Geen resultaten voor “{{query}}”.', {
                query: trimmedQuery,
              })}
            </S.EmptySubtitle>
          </S.EmptyState>
        )}

        {nonEmptyGroups.map((group) => (
          <S.Group key={group.groupKey}>
            <S.GroupTitle>{t(`search:common.tabs.${group.groupKey}`, group.title)}</S.GroupTitle>

            <S.GroupList>
              {group.items.map((item) => {
                flatIndexCounter += 1;
                const flatIndex = flatIndexCounter;
                const isActive = flatIndex === activeResultIndex;

                return (
                  <S.Row
                    key={item.id}
                    type="button"
                    onClick={() => {
                      onClose();
                      navigate(item.link);
                    }}
                    ref={(element) => setRowElementByIndex(flatIndex, element)}
                    aria-selected={isActive}
                    $active={isActive}
                    tabIndex={-1}
                  >
                    <S.RowLeft>
                      <S.RowIcon>
                        <FontAwesomeIcon icon={group.icon} size="lg" />
                      </S.RowIcon>

                      <S.RowText>
                        <S.RowTitle>{item.title}</S.RowTitle>
                        <S.RowSubtitle>
                          {item.subtitle?.map((subItem, index) => (
                            <React.Fragment key={`${subItem.type}-${index}`.toString()}>
                              {index > 0 && <span aria-hidden> · </span>}

                              {subItem.type === 'status' && (
                                <S.SubItem>
                                  <S.StatusDot $variant={subItem.variant} aria-hidden />
                                  <span>{subItem.label}</span>
                                </S.SubItem>
                              )}

                              {subItem.type !== 'status' && (
                                <S.SubItem>
                                  <span>{subItem.label}</span>
                                </S.SubItem>
                              )}
                            </React.Fragment>
                          ))}
                        </S.RowSubtitle>
                      </S.RowText>
                    </S.RowLeft>

                    <S.RowRight data-row-right aria-hidden>
                      ↗
                    </S.RowRight>
                  </S.Row>
                );
              })}
            </S.GroupList>
          </S.Group>
        ))}
      </S.ResultsScroll>

      {showRefreshOverlay && <S.ResultsOverlay />}
    </S.Body>
  );
};

Results.propTypes = {
  visibleGroups: PropTypes.array.isRequired,
  activeResultIndex: PropTypes.number.isRequired,
  setRowElementByIndex: PropTypes.func.isRequired,
  query: PropTypes.string.isRequired,
  isSearching: PropTypes.bool.isRequired,
  hasSearched: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default Results;
